import React from 'react';
import TelegramShareIcon from './vectors/TelegramShareIcon';
import FacebookShareIcon from './vectors/FacebookShareIcon';
import WhatsAppShareIcon from './vectors/WhatsAppShareIcon';
import TwitterShareIcon from './vectors/TwitterShareIcon';
import LinkedInShareIcon from './vectors/LinkedInShareIcon';
import type { StackProps } from '@mui/material/Stack';
import Stack from '@mui/material/Stack';
import ButtonBase from '@mui/material/ButtonBase';
import type { SystemStyleObject } from '@mui/system/styleFunctionSx';
import type { Theme } from '@mui/material/styles';
import TelegramShareIconOutlined from './vectors/TelegramShareIconOutlined';
import FacebookShareIconOutlined from './vectors/FacebookShareIconOutlined';
import WhatsAppShareIconOutlined from './vectors/WhatsAppShareIconOutlined';
import TwitterShareIconOutlined from './vectors/TwitterShareIconOutlined';
import LinkedInShareIconOutlined from './vectors/LinkedInShareIconOutlined';

const socials = [
  {
    type: 'telegram',
    icon: <TelegramShareIcon color="primary" />,
    iconOutlined: <TelegramShareIconOutlined color="secondary" fontSize="small" />,
    url: (url: string, text?: string) =>
      `https://t.me/share/url?url=${url}${encodeURI('\n')}${text ? `&text=${text}` : ''}`,
  },
  {
    type: 'facebook',
    icon: <FacebookShareIcon color="primary" />,
    iconOutlined: <FacebookShareIconOutlined color="secondary" fontSize="small" />,
    url: (url: string, text?: string) =>
      `https://www.facebook.com/sharer/sharer.php?u=${url}${text ? `&text=${text}` : ''}`,
  },
  {
    type: 'whatsapp',
    icon: <WhatsAppShareIcon color="primary" />,
    iconOutlined: <WhatsAppShareIconOutlined color="secondary" fontSize="small" />,
    url: (url: string, text?: string) =>
      `https://api.whatsapp.com/send?text=${url}${encodeURI('\n')}${text}`,
  },
  {
    type: 'twitter',
    icon: <TwitterShareIcon color="primary" />,
    iconOutlined: <TwitterShareIconOutlined color="secondary" fontSize="small" />,
    url: (url: string, text?: string) =>
      `https://twitter.com/intent/tweet?url=${url}${text ? `&text=${text}` : ''}`,
  },
  {
    type: 'linkedin',
    icon: <LinkedInShareIcon color="primary" />,
    iconOutlined: <LinkedInShareIconOutlined color="secondary" fontSize="small" />,
    url: (url: string, text?: string) =>
      `https://www.linkedin.com/shareArticle?mini=true&url=${url}${text ? `&text=${text}` : ''}`,
  },
];

interface Props {
  url: string;
  text?: string;
  gap?: StackProps['gap'];
  buttonSx?: SystemStyleObject<Theme>;
  variant?: 'contained' | 'outlined';
}

export default function ShareButtonsWidget(props: Props) {
  const { url, gap, text, buttonSx, variant = 'contained' } = props;

  return (
    <Stack
      width="100%"
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={gap || { sm: 3 }}
    >
      {socials.map((social) => (
          <ButtonBase
            LinkComponent="a"
            target="_blank"
            rel="noopener noreferrer"
            href={social.url(url, text)}
            key={social.type}
            sx={[
              {
                borderRadius: '8px',
                bgcolor: 'primary.main',
                px: '16px',
                py: '10px',
              },
              variant === 'outlined' && {
                bgcolor: 'transparent',
                border: '1px solid',
                borderColor: 'outline.medium',
              },
              ...(buttonSx ? [buttonSx] : []),
            ]}
          >
            {variant === 'outlined' ? social.iconOutlined : social.icon}
          </ButtonBase>
        ))}
    </Stack>
  );
}
